import { useCallback, useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'
import {
  CheckCircle2,
  Newspaper,
  Plus,
  RotateCcw,
  Send,
  Sparkles,
  Trash2,
  Undo2,
} from 'lucide-react'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'
import type { RoundEdits } from '../api/worldGraph'
import type { WorldGraphController } from '../hooks/useWorldGraph'
import { NEGATIVE, POSITIVE } from '../graphColors'
import type {
  ArtifactAudience,
  RoomTeam,
  RoundArtifact,
  TeamProposal,
  WorldGraphNode,
  WorldGraphRound,
  WorldGraphState,
} from '../types'
import { BuildProgress, LastRunAlert, RunSummary } from './BuildStatus'
import { useConfirm } from './ConfirmDialog'

const round1 = (value: number) => Math.round(value * 10) / 10

function signed(value: number) {
  return `${value > 0 ? '+' : ''}${round1(value)}`
}

/** Green when the real world improves, red when it worsens. */
function changeColor(delta: number, inverted: boolean) {
  if (!delta) return undefined
  return (inverted ? delta < 0 : delta > 0) ? POSITIVE : NEGATIVE
}

// ── A team's proposed changes ────────────────────────────────────────────────

interface TeamCardProps {
  group: TeamProposal
  nodes: Map<string, WorldGraphNode>
  editable: boolean
  busy: boolean
  onSave: (edit: {
    key: string
    include: boolean
    nodes_delta: Record<string, number>
  }) => Promise<boolean>
  onDirty: (key: string, dirty: boolean) => void
}

function TeamCard({
  group,
  nodes,
  editable,
  busy,
  onSave,
  onDirty,
}: TeamCardProps) {
  const [delta, setDelta] = useState<Record<string, string>>(() =>
    toStrings(group.nodes_delta)
  )
  const [include, setInclude] = useState(group.include)
  const savedKey = JSON.stringify([group.nodes_delta, group.include])
  const [seen, setSeen] = useState(savedKey)
  if (seen !== savedKey) {
    setSeen(savedKey)
    setDelta(toStrings(group.nodes_delta))
    setInclude(group.include)
  }
  const parsed = Object.fromEntries(
    Object.entries(delta)
      .map(([id, v]) => [id, Number(v)] as const)
      .filter(([, v]) => Number.isFinite(v) && v !== 0)
  )
  const dirty =
    include !== group.include ||
    JSON.stringify(parsed) !== JSON.stringify(group.nodes_delta)
  useEffect(() => onDirty(group.key, dirty), [group.key, dirty, onDirty])
  const reasons = new Map(group.justifications.map((j) => [j.node_id, j]))
  const addable = [...nodes.values()].filter(
    (n) => n.type !== 'Drifter' && !(n.id in delta)
  )

  return (
    <div
      className={cn(
        'space-y-3 rounded-lg border p-4',
        !include && 'bg-muted/40 opacity-75'
      )}
      data-testid='world-round-team'
    >
      <div className='flex flex-wrap items-center justify-between gap-2'>
        <div>
          <div className='font-medium text-dark-blue'>{group.team_name}</div>
          {group.roster_team_name &&
            group.roster_team_name !== group.team_name && (
              <div className='text-xs text-muted-foreground'>
                plays {group.roster_team_name}
              </div>
            )}
        </div>
        <div className='flex items-center gap-3'>
          {group.status === 'failed' && (
            <Badge
              variant='outline'
              className='border-amber-300 text-amber-800'
            >
              No proposal
            </Badge>
          )}
          {group.status === 'no_team' && (
            <Badge
              variant='outline'
              className='border-amber-300 text-amber-800'
            >
              Not in a roster team
            </Badge>
          )}
          {group.status === 'waiting' && (
            <Badge
              variant='outline'
              className='border-amber-300 text-amber-800'
            >
              Waiting for a ruling
            </Badge>
          )}
          {group.roster_team && (
            <label className='inline-flex items-center gap-2 text-sm'>
              <input
                type='checkbox'
                checked={include}
                disabled={!editable || busy}
                onChange={(e) => setInclude(e.target.checked)}
                aria-label={`Apply ${group.team_name}'s changes`}
              />
              Apply these changes
            </label>
          )}
        </div>
      </div>

      <div className='space-y-2'>
        {group.decisions.map((decision) => (
          <blockquote
            key={decision.id}
            className='border-l-2 border-gray-200 pl-3 text-sm'
          >
            <div className='text-xs text-muted-foreground'>
              {decision.student}
              {decision.late && (
                <span className='ml-2 text-amber-700'>Submitted late</span>
              )}
              {!decision.adjudication && (
                <span className='ml-2 text-amber-700'>
                  Waiting for a ruling
                </span>
              )}
            </div>
            <p className='whitespace-pre-wrap'>{decision.text}</p>
            {decision.adjudication && (
              <div className='mt-1 rounded bg-slate-50 px-2 py-1'>
                <div className='text-xs font-medium text-muted-foreground'>
                  Ruling, which moves the world
                </div>
                <p className='whitespace-pre-wrap'>{decision.adjudication}</p>
              </div>
            )}
          </blockquote>
        ))}
      </div>

      {group.error && <p className='text-sm text-amber-800'>{group.error}</p>}

      {group.roster_team && (
        <div className='space-y-2'>
          {Object.keys(delta).length === 0 ? (
            <p className='text-sm text-muted-foreground'>
              {group.status === 'pending'
                ? 'Waiting for the proposal.'
                : 'No stock changes. This team will not move the world this round.'}
            </p>
          ) : (
            <table className='w-full text-sm'>
              <thead className='text-left text-xs uppercase text-muted-foreground'>
                <tr>
                  <th className='py-1 pr-2'>Stock</th>
                  <th className='py-1 pr-2'>Change</th>
                  {editable && (
                    <th className='whitespace-nowrap py-1 pr-2'>Now → after</th>
                  )}
                  <th className='py-1' />
                </tr>
              </thead>
              <tbody>
                {Object.entries(delta).map(([id, value]) => {
                  const node = nodes.get(id)
                  const amount = Number(value) || 0
                  const reason = reasons.get(id)
                  return (
                    <tr key={id} className='border-t align-top'>
                      <td className='py-2 pr-2'>
                        <div className='font-medium text-dark-blue'>
                          {node?.label ?? id}
                        </div>
                        {reason?.decision_ref && (
                          <div className='text-xs italic text-muted-foreground'>
                            “{reason.decision_ref}”
                          </div>
                        )}
                        {reason?.rationale && (
                          <div className='text-xs text-muted-foreground'>
                            {reason.rationale}
                          </div>
                        )}
                        {reason?.cross_team && (
                          <div className='text-xs text-amber-700'>
                            Reaches into another team's stock
                          </div>
                        )}
                      </td>
                      <td className='py-2 pr-2'>
                        <Input
                          type='number'
                          className='h-8 w-20'
                          value={value}
                          disabled={!editable || busy}
                          aria-label={`Change to ${node?.label ?? id} for ${group.team_name}`}
                          onChange={(e) =>
                            setDelta((d) => ({ ...d, [id]: e.target.value }))
                          }
                        />
                      </td>
                      {/* Once applied, the values already include the change. */}
                      {editable && (
                        <td className='whitespace-nowrap py-2 pr-2 tabular-nums'>
                          {node && (
                            <span
                              style={{
                                color: changeColor(amount, node.inverted),
                              }}
                            >
                              {round1(node.value)} →{' '}
                              {round1(
                                Math.max(0, Math.min(100, node.value + amount))
                              )}
                            </span>
                          )}
                        </td>
                      )}
                      <td className='py-2 text-right'>
                        {editable && (
                          <Button
                            size='icon'
                            variant='ghost'
                            disabled={busy}
                            aria-label={`Remove ${node?.label ?? id} from ${group.team_name}`}
                            onClick={() =>
                              setDelta((d) => {
                                const next = { ...d }
                                delete next[id]
                                return next
                              })
                            }
                          >
                            <Trash2 className='h-4 w-4' aria-hidden />
                          </Button>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}

          {editable && (
            <div className='flex flex-wrap items-center justify-between gap-2'>
              <label className='inline-flex items-center gap-2 text-sm text-muted-foreground'>
                <Plus className='h-4 w-4' aria-hidden />
                <select
                  className='h-8 max-w-xs rounded-md border border-input bg-background px-2 text-sm'
                  value=''
                  disabled={busy}
                  aria-label={`Add a stock for ${group.team_name}`}
                  onChange={(e) =>
                    e.target.value &&
                    setDelta((d) => ({ ...d, [e.target.value]: '5' }))
                  }
                >
                  <option value=''>Add a stock…</option>
                  {addable.map((n) => (
                    <option key={n.id} value={n.id}>
                      {n.label}
                    </option>
                  ))}
                </select>
              </label>
              {dirty && (
                <Button
                  size='sm'
                  className='bg-dark-blue hover:bg-dark-blue-hovered'
                  disabled={busy}
                  onClick={() =>
                    onSave({ key: group.key, include, nodes_delta: parsed })
                  }
                >
                  Save {group.team_name}
                </Button>
              )}
            </div>
          )}

          {group.skipped.length > 0 && (
            <details className='text-xs text-muted-foreground'>
              <summary className='cursor-pointer'>
                Not turned into changes ({group.skipped.length})
              </summary>
              <ul className='mt-1 list-disc space-y-0.5 pl-5'>
                {group.skipped.map((item, index) => (
                  <li key={index}>
                    {item.reference}
                    {item.reason ? `: ${item.reason}` : ''}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}
    </div>
  )
}

function toStrings(delta: Record<string, number>) {
  return Object.fromEntries(
    Object.entries(delta).map(([id, v]) => [id, String(v)])
  )
}

// ── What applying the round did ──────────────────────────────────────────────

const SOURCE_LABELS: Record<string, string> = {
  player_decision: 'decisions',
  white_cell_event: 'White Cell',
  tick_rule: 'drift',
  propagation: 'knock-on effects',
}

function AppliedSummary({
  record,
  nodes,
}: {
  record: WorldGraphRound
  nodes: Map<string, WorldGraphNode>
}) {
  const moves = useMemo(() => {
    const byNode = new Map<
      string,
      { from: number; to: number; sources: Set<string> }
    >()
    for (const entry of record.log) {
      if (entry.old_value === undefined || entry.new_value === undefined)
        continue
      const move = byNode.get(entry.node_id) ?? {
        from: entry.old_value,
        to: entry.new_value,
        sources: new Set<string>(),
      }
      move.to = entry.new_value
      move.sources.add(entry.change_source)
      byNode.set(entry.node_id, move)
    }
    return [...byNode.entries()]
      .map(([id, move]) => ({ id, ...move, delta: move.to - move.from }))
      .filter((move) => Math.abs(move.delta) >= 0.05)
      .sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta))
  }, [record.log])

  if (!moves.length) {
    return (
      <p className='text-sm text-muted-foreground'>
        Nothing was approved, so the world did not move this round.
      </p>
    )
  }
  return (
    <div className='space-y-2' data-testid='world-round-moves'>
      <p className='text-sm text-muted-foreground'>
        {moves.length} stocks moved. The biggest changes:
      </p>
      <ul className='space-y-1 text-sm'>
        {moves.slice(0, 12).map((move) => {
          const node = nodes.get(move.id)
          return (
            <li key={move.id} className='flex flex-wrap gap-x-2'>
              <span className='font-medium text-dark-blue'>
                {node?.label ?? move.id}
              </span>
              <span
                className='tabular-nums'
                style={{
                  color: changeColor(move.delta, node?.inverted ?? false),
                }}
              >
                {round1(move.from)} → {round1(move.to)} ({signed(move.delta)})
              </span>
              <span className='text-xs text-muted-foreground'>
                from{' '}
                {[...move.sources]
                  .map((s) => SOURCE_LABELS[s] ?? s)
                  .join(' and ')}
              </span>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

// ── News and briefs ──────────────────────────────────────────────────────────

interface DraftCardProps {
  draft: RoundArtifact
  teams: RoomTeam[]
  busy: boolean
  onSave: (edit: {
    id: string
    title: string
    text: string
    include: boolean
    audience: ArtifactAudience
  }) => Promise<boolean>
  onDirty: (key: string, dirty: boolean) => void
}

function DraftCard({ draft, teams, busy, onSave, onDirty }: DraftCardProps) {
  const [title, setTitle] = useState(draft.title)
  const [text, setText] = useState(draft.text)
  const [include, setInclude] = useState(draft.include)
  const [audience, setAudience] = useState(draft.audience)
  const savedKey = JSON.stringify(draft)
  const [seen, setSeen] = useState(savedKey)
  if (seen !== savedKey) {
    setSeen(savedKey)
    setTitle(draft.title)
    setText(draft.text)
    setInclude(draft.include)
    setAudience(draft.audience)
  }
  const released = Boolean(draft.event_id)
  const dirty =
    title !== draft.title ||
    text !== draft.text ||
    include !== draft.include ||
    JSON.stringify(audience) !== JSON.stringify(draft.audience)
  useEffect(
    () => onDirty(draft.id, dirty && !released),
    [draft.id, dirty, released, onDirty]
  )
  const toggleTeam = (id: number) =>
    setAudience((a) => {
      const teamsChosen = a.teams.includes(id)
        ? a.teams.filter((t) => t !== id)
        : [...a.teams, id]
      return { all: false, teams: teamsChosen }
    })

  return (
    <div
      className={cn(
        'space-y-3 rounded-lg border p-4',
        !include && !released && 'bg-muted/40 opacity-75'
      )}
      data-testid='world-round-draft'
    >
      <div className='flex flex-wrap items-center justify-between gap-2'>
        <Badge variant='secondary'>{draft.product_type}</Badge>
        {released ? (
          <Badge
            variant='outline'
            className='gap-1 border-green-600/30 bg-green-50 text-green-700'
          >
            <CheckCircle2 className='h-3.5 w-3.5' aria-hidden />
            Released
          </Badge>
        ) : (
          <label className='inline-flex items-center gap-2 text-sm'>
            <input
              type='checkbox'
              checked={include}
              disabled={busy}
              onChange={(e) => setInclude(e.target.checked)}
              aria-label={`Release ${draft.title}`}
            />
            Release this
          </label>
        )}
      </div>
      <Input
        value={title}
        disabled={released || busy}
        aria-label='Headline'
        onChange={(e) => setTitle(e.target.value)}
        className='font-medium'
      />
      <Textarea
        value={text}
        disabled={released || busy}
        aria-label={`Text of ${draft.title}`}
        onChange={(e) => setText(e.target.value)}
        className='min-h-[120px] text-sm'
      />
      <div className='flex flex-wrap items-center gap-3 text-sm'>
        <span className='text-muted-foreground'>Goes to:</span>
        <label className='inline-flex items-center gap-1'>
          <input
            type='radio'
            checked={audience.all}
            disabled={released || busy}
            onChange={() => setAudience({ all: true, teams: [] })}
          />
          All players
        </label>
        {teams.map((team) => (
          <label key={team.id} className='inline-flex items-center gap-1'>
            <input
              type='checkbox'
              checked={!audience.all && audience.teams.includes(team.id)}
              disabled={released || busy}
              onChange={() => toggleTeam(team.id)}
            />
            {team.name}
          </label>
        ))}
      </div>
      {!released && dirty && (
        <div className='flex justify-end'>
          <Button
            size='sm'
            className='bg-dark-blue hover:bg-dark-blue-hovered'
            disabled={busy || !title.trim() || !text.trim()}
            onClick={() =>
              onSave({ id: draft.id, title, text, include, audience })
            }
          >
            Save
          </Button>
        </div>
      )}
    </div>
  )
}

// ── The panel ────────────────────────────────────────────────────────────────

interface Props {
  state: WorldGraphState
  wg: WorldGraphController
}

/**
 * Playing a round: the teams' decisions become proposed stock changes, the
 * professor applies them, and the White Cell writes news and briefs about what
 * moved for the professor to release to players.
 */
export default function RoundPanel({ state, wg }: Props) {
  const numbers = useMemo(() => {
    const all = new Set([
      ...state.round_options.map((o) => o.round_number),
      ...state.rounds.map((r) => r.round_number),
    ])
    return [...all].sort((a, b) => a - b)
  }, [state.round_options, state.rounds])
  const firstOpen =
    numbers.find(
      (n) =>
        state.rounds.find((r) => r.round_number === n)?.status !== 'applied'
    ) ?? numbers[numbers.length - 1]
  const [picked, setPicked] = useState<number | null>(null)
  const selected = picked ?? firstOpen
  const [busy, setBusy] = useState(false)
  const [unsaved, setUnsaved] = useState<Set<string>>(new Set())
  const { confirm, dialog } = useConfirm()
  const onDirty = useCallback((key: string, dirty: boolean) => {
    setUnsaved((current) => {
      if (current.has(key) === dirty) return current
      const next = new Set(current)
      if (dirty) next.add(key)
      else next.delete(key)
      return next
    })
  }, [])

  const nodes = useMemo(
    () => new Map(state.graph.nodes.map((n) => [n.id, n])),
    [state.graph.nodes]
  )
  const record = state.rounds.find((r) => r.round_number === selected)
  const option = state.round_options.find((o) => o.round_number === selected)
  const build =
    state.current_build && state.current_build.round_number === selected
      ? state.current_build
      : null
  const running = build?.status === 'running'
  const anyRunning = state.current_build?.status === 'running'
  const lastFailed =
    build && (build.status === 'failed' || build.status === 'interrupted')
      ? build
      : null

  const run = async (action: () => Promise<boolean>) => {
    setBusy(true)
    const ok = await action()
    setBusy(false)
    return ok
  }
  const edit = (edits: RoundEdits) => run(() => wg.editRound(selected!, edits))

  if (!numbers.length || selected === undefined) {
    return (
      <Card className='bg-white shadow-md' data-testid='world-rounds'>
        <CardContent className='p-6 text-sm text-muted-foreground'>
          No decisions have been submitted yet. Once players submit decisions in
          a round, you turn them into changes to the world here.
        </CardContent>
      </Card>
    )
  }

  const applied = record?.status === 'applied'
  const unsavedTeams =
    record?.proposals.some((g) => unsaved.has(g.key)) ?? false
  const unsavedDrafts =
    record?.artifacts.some((a) => unsaved.has(a.id)) ?? false
  const released = record?.artifacts.filter((a) => a.event_id) ?? []
  const toRelease =
    record?.artifacts.filter((a) => a.include && !a.event_id) ?? []

  const propose = async () => {
    if (
      record?.proposals.length &&
      !(await confirm({
        title: 'Propose the changes again?',
        description: 'The new proposals replace these, including your edits.',
        confirmLabel: 'Propose again',
        destructive: true,
      }))
    )
      return
    run(() => wg.roundAction(selected, 'propose'))
  }
  const apply = async () => {
    if (
      !(await confirm({
        title: `Apply round ${selected}?`,
        description:
          'The approved changes, the drift rules and their knock-on effects move the world.',
        confirmLabel: 'Apply changes',
      }))
    )
      return
    if (await run(() => wg.roundAction(selected, 'apply')))
      toast.success(`Round ${selected} applied: the world has moved.`)
  }
  const undo = async () => {
    if (
      await confirm({
        title: `Undo round ${selected}?`,
        description: 'Every stock goes back to where it was before it.',
        confirmLabel: 'Undo round',
      })
    )
      run(() => wg.roundAction(selected, 'undo'))
  }
  const write = async () => {
    if (
      record?.artifacts.length &&
      !(await confirm({
        title: 'Write the news again?',
        description: 'The new drafts replace these, including your edits.',
        confirmLabel: 'Write again',
        destructive: true,
      }))
    )
      return
    run(() => wg.roundAction(selected, 'write'))
  }
  const release = async () => {
    const items = `${toRelease.length} item${toRelease.length === 1 ? '' : 's'}`
    if (
      !(await confirm({
        title: `Release ${items} to players now?`,
        description: "Released news can't be changed.",
        confirmLabel: 'Release',
      }))
    )
      return
    if (
      await run(() =>
        wg.roundAction(
          selected,
          'release',
          toRelease.map((a) => a.id)
        )
      )
    )
      toast.success(`Released ${items} to players.`)
  }
  const resetWorld = async () => {
    if (
      await confirm({
        title: 'Start the world over?',
        description:
          'Every stock goes back to its starting value and every round here is forgotten. News already released stays with the players.',
        confirmLabel: 'Start over',
        destructive: true,
      })
    )
      run(() => wg.resetWorld())
  }

  return (
    <div className='space-y-6' data-testid='world-rounds'>
      {dialog}
      <Card className='bg-white shadow-md'>
        <CardHeader className='pb-3'>
          <CardTitle className='text-base text-dark-blue'>Rounds</CardTitle>
          <CardDescription>
            Each round, the teams' decisions become proposed changes to the
            world. You review and apply them, then release the news and briefs
            the White Cell writes about what moved.
          </CardDescription>
        </CardHeader>
        <CardContent className='flex flex-wrap items-center gap-2'>
          {numbers.map((n) => {
            const status = state.rounds.find(
              (r) => r.round_number === n
            )?.status
            const count = state.round_options.find(
              (o) => o.round_number === n
            )?.decisions
            return (
              <Button
                key={n}
                size='sm'
                variant={n === selected ? 'default' : 'outline'}
                className={cn(
                  n === selected && 'bg-dark-blue hover:bg-dark-blue-hovered'
                )}
                aria-pressed={n === selected}
                onClick={() => setPicked(n)}
              >
                Round {n}
                {status === 'applied' ? ' ✓' : ''}
                {count !== undefined && (
                  <span className='ml-1 text-xs opacity-75'>
                    ({count} decision{count === 1 ? '' : 's'})
                  </span>
                )}
              </Button>
            )
          })}
          <Button
            size='sm'
            variant='ghost'
            className='ml-auto text-muted-foreground'
            disabled={busy || anyRunning || !state.rounds.length}
            onClick={resetWorld}
          >
            <RotateCcw className='h-4 w-4' aria-hidden />
            Start the world over
          </Button>
        </CardContent>
      </Card>

      <Card className='bg-white shadow-md'>
        <CardHeader>
          <div className='flex flex-wrap items-start justify-between gap-2'>
            <div>
              <CardTitle className='text-base text-dark-blue'>
                1. Changes from round {selected}'s decisions
              </CardTitle>
              <CardDescription>
                {option?.decisions ?? 0} decision
                {option?.decisions === 1 ? '' : 's'} this round. The AI proposes
                each team's direct changes from your rulings on its decisions; a
                decision without a ruling waits. The world's knock-on effects
                follow when you apply the round.
              </CardDescription>
            </div>
            {applied && (
              <Badge
                variant='outline'
                className='gap-1 border-green-600/30 bg-green-50 text-green-700'
              >
                <CheckCircle2 className='h-3.5 w-3.5' aria-hidden />
                Applied
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent className='space-y-4'>
          {lastFailed && lastFailed.step === 'proposals' && (
            <LastRunAlert build={lastFailed} />
          )}
          {running && build?.step === 'proposals' ? (
            <BuildProgress build={build} />
          ) : !record?.proposals.length ? (
            <div className='rounded-lg border border-dashed border-gray-300 p-6'>
              <p className='text-sm text-muted-foreground'>
                {option?.decisions
                  ? 'Adjudicate the decisions first: the AI reads only your rulings and proposes how they change the world, for you to review.'
                  : 'No decisions have been submitted for this round yet.'}
              </p>
              <Button
                className='mt-4 bg-dark-blue hover:bg-dark-blue-hovered'
                disabled={busy || anyRunning || !option?.decisions}
                onClick={propose}
              >
                <Sparkles className='h-4 w-4' aria-hidden />
                Propose changes
              </Button>
            </div>
          ) : (
            <>
              {build?.step === 'proposals' && build.status === 'succeeded' && (
                <RunSummary build={build} verb='Proposed' />
              )}
              {applied && record && (
                <AppliedSummary record={record} nodes={nodes} />
              )}
              <div className='space-y-3'>
                {record.proposals.map((group) => (
                  <TeamCard
                    key={group.key}
                    group={group}
                    nodes={nodes}
                    editable={!applied}
                    busy={busy || anyRunning}
                    onSave={(proposal) => edit({ proposals: [proposal] })}
                    onDirty={onDirty}
                  />
                ))}
              </div>
              <div className='flex flex-wrap items-center justify-end gap-2'>
                {!applied && unsavedTeams && (
                  <span className='text-sm text-amber-700'>
                    Save your changes before applying the round.
                  </span>
                )}
                {applied ? (
                  <Button
                    variant='outline'
                    disabled={busy || anyRunning || released.length > 0}
                    title={
                      released.length
                        ? 'News from this round has been released'
                        : undefined
                    }
                    onClick={undo}
                  >
                    <Undo2 className='h-4 w-4' aria-hidden />
                    Undo round {selected}
                  </Button>
                ) : (
                  <>
                    <Button
                      variant='outline'
                      disabled={busy || anyRunning}
                      onClick={propose}
                    >
                      <Sparkles className='h-4 w-4' aria-hidden />
                      Propose again
                    </Button>
                    <Button
                      className='bg-dark-blue hover:bg-dark-blue-hovered'
                      disabled={busy || anyRunning || unsavedTeams}
                      onClick={apply}
                    >
                      <CheckCircle2 className='h-4 w-4' aria-hidden />
                      Apply round {selected}
                    </Button>
                  </>
                )}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {applied && record && (
        <Card className='bg-white shadow-md' data-testid='world-round-news'>
          <CardHeader>
            <CardTitle className='text-base text-dark-blue'>
              2. News and briefs
            </CardTitle>
            <CardDescription>
              The White Cell writes about what players would notice, and why it
              happened, without numbers. Edit anything, choose who receives each
              item, then release them into the players' event feed.
            </CardDescription>
          </CardHeader>
          <CardContent className='space-y-4'>
            {lastFailed && lastFailed.step === 'artifacts' && (
              <LastRunAlert build={lastFailed} />
            )}
            {running && build?.step === 'artifacts' ? (
              <BuildProgress build={build} />
            ) : !record.artifacts.length ? (
              <div className='rounded-lg border border-dashed border-gray-300 p-6'>
                <p className='text-sm text-muted-foreground'>
                  {build?.step === 'artifacts' && build.status === 'succeeded'
                    ? 'Nothing moved enough this round for players to notice.'
                    : 'Write news and briefs about what this round changed.'}
                </p>
                <Button
                  className='mt-4 bg-dark-blue hover:bg-dark-blue-hovered'
                  disabled={busy || anyRunning}
                  onClick={write}
                >
                  <Newspaper className='h-4 w-4' aria-hidden />
                  Write news and briefs
                </Button>
              </div>
            ) : (
              <>
                {build?.step === 'artifacts' &&
                  build.status === 'succeeded' && (
                    <RunSummary build={build} verb='Written' />
                  )}
                <div className='space-y-3'>
                  {record.artifacts.map((draft) => (
                    <DraftCard
                      key={draft.id}
                      draft={draft}
                      teams={state.team_setup.room_teams}
                      busy={busy || anyRunning}
                      onSave={(artifact) => edit({ artifacts: [artifact] })}
                      onDirty={onDirty}
                    />
                  ))}
                </div>
                <div className='flex flex-wrap items-center justify-end gap-2'>
                  {unsavedDrafts && (
                    <span className='text-sm text-amber-700'>
                      Save your edits before releasing.
                    </span>
                  )}
                  {released.length > 0 && (
                    <span className='text-sm text-muted-foreground'>
                      {released.length} released
                    </span>
                  )}
                  {released.length === 0 && (
                    <Button
                      variant='outline'
                      disabled={busy || anyRunning}
                      onClick={write}
                    >
                      <Sparkles className='h-4 w-4' aria-hidden />
                      Write again
                    </Button>
                  )}
                  <Button
                    className='bg-dark-blue hover:bg-dark-blue-hovered'
                    disabled={
                      busy || anyRunning || !toRelease.length || unsavedDrafts
                    }
                    onClick={release}
                  >
                    <Send className='h-4 w-4' aria-hidden />
                    Release {toRelease.length} to players
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  )
}
